import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import Header from '../components/Header';
import TabBar from '../components/TabBar';
import TaxiLoader from '../components/TaxiLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMoneyBillWave, 
  faExchangeAlt, 
  faGasPump, 
  faChevronLeft, 
  faChevronRight,
  faCar,
  faCreditCard,
  faClipboardList,
  faUserTie,
  faPlane,
  faCloudSun,
  faMapMarkedAlt,
  faCalculator,
  faBullhorn,
  faPhoneAlt,
  faUserShield,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Home.css';
import { 
  getBorclar, 
  getUserInfo, 
  isLoggedIn, 
  clearUserInfo, 
  refreshUserSession,
  getYakitHareketleri,
  getPlakaGecisleri,
  YakitHareket,
  PlakaGecis
} from '../services/authService';
import type { Kullanici } from '../services/authService';

// Kullanici tipini genişlet
interface BorcSofor {
  adsoyad?: string;
  ad?: string;
  bakiye?: number;
  borc?: number;
}

interface BorcDetay {
  COUNTER?: number;
  TARIH?: string;
  ACIKLAMA?: string;
  BORC?: number;
  ALACAK?: number;
  uyeid?: number;
  borc_id?: string;
  borc_tarihi?: string;
  borc_tutari?: number;
  borc_aciklamasi?: string;
  odeme_durumu?: string;
}

interface KullaniciGorunum {
  uyeid: number;
  ADI: string;
  SOYADI: string;
  GRUBU: string;
  DURUMU: string;
  KARTRESIM?: string;
  UYENO: number;
  borclar: BorcDetay[];
  toplam_borc: number;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [kullanici, setKullanici] = useState<KullaniciGorunum[]>([]);
  const [bildirimSayisi, setBildirimSayisi] = useState<number>(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionInfo, setSessionInfo] = useState({
    uyeId: '',
    uyeno: '',
    kimlikno: '',
    grubu: '',
    durumu: ''
  });
  const [activeTab, setActiveTab] = useState<'borclar' | 'plaka' | 'yakit'>('borclar');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [cardWidth, setCardWidth] = useState(340);
  const [cardGap, setCardGap] = useState(24);

  // Yakıt ve plaka verileri için state'ler
  const [yakitHareketleri, setYakitHareketleri] = useState<YakitHareket[]>([]);
  const [plakaGecisleri, setPlakaGecisleri] = useState<PlakaGecis[]>([]);
  const [yakitOzet, setYakitOzet] = useState({ toplam_litre: 0, toplam_tutar: 0, hareket_sayisi: 0 });
  const [plakaOzet, setPlakaOzet] = useState({ giris_sayisi: 0, cikis_sayisi: 0, toplam_gecis: 0 });
  const [selectedPeriod, setSelectedPeriod] = useState<'gunluk' | 'haftalik' | 'aylik'>('gunluk');
  const [userPlaka, setUserPlaka] = useState<string>('');

  // Login durumu kontrolü - sadece ilk yüklemede kontrol et
  useEffect(() => {
    const checkLoginStatus = () => {
      const loginStatus = isLoggedIn();
      
      if (loginStatus) {
        const userInfo = getUserInfo();
        if (userInfo && userInfo.uyeid) {
          // Session'ı yenile
          refreshUserSession();
          // Kullanıcı bilgilerini session'a kaydet
          sessionStorage.setItem('uyeId', userInfo.uyeid || '');
          sessionStorage.setItem('uyeno', userInfo.uyeno || '');
          sessionStorage.setItem('kimlikno', userInfo.kimlikno || '');
          sessionStorage.setItem('grubu', userInfo.grubu || '');
          sessionStorage.setItem('adSoyad', `${userInfo.ADI || ''} ${userInfo.SOYADI || ''}`.trim());
          
          // Sadece authentication durumu değiştiyse state'i güncelle
          if (!isAuthenticated) {
            setIsAuthenticated(true);
            console.log('User authenticated, fetching data...');
          // Kullanıcı bilgilerini fetch et
            fetchKullaniciBilgileri(userInfo.uyeid);
          }
        }
      } else {
        // Sadece authentication durumu değiştiyse state'i güncelle
        if (isAuthenticated) {
          console.log('User not authenticated, clearing state...');
        setIsAuthenticated(false);
          setKullanici([]);
        }
      }
    };

    // Sadece ilk yüklemede kontrol et
    checkLoginStatus();

    // Window focus olduğunda kontrol et (ama sürekli değil)
    const handleFocus = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []); // Dependency array boş - sadece mount/unmount'ta çalışsın

  // Kullanıcı bilgilerini getir
  const fetchKullaniciBilgileri = async (uyeId: string) => {
    // Eğer zaten loading durumundaysa, tekrar çağırma
    if (loading) return;
    
    setLoading(true);
    try {
      // Session'dan diğer parametreleri al
      const userInfo = sessionStorage.getItem('userInfo');
      let uyeno = '', kimlikno = '', grubu = '', plaka = '';
      if (userInfo) {
        const user = JSON.parse(userInfo);
        uyeno = user.uyeno || user.uyeNo || '';
        kimlikno = user.kimlikno || user.kimlikNo || '';
        grubu = user.grubu || user.grup || '';
        plaka = user.plaka || '';
        console.log('Session userInfo:', user);
        console.log('Extracted plaka:', plaka);
      }
      
      // Parametreleri eksiksiz gönder
      const response = await getBorclar(uyeId, uyeno, kimlikno, grubu);
      if (response.success && response.data?.borclar && response.data.borclar.length > 0) {
        // API'den gelen veriyi KullaniciGorunum tipine dönüştür
        const mapped = response.data.borclar.map((item: any) => ({
          uyeid: Number(item.uyeid),
          ADI: item.ADI,
          SOYADI: item.SOYADI,
          GRUBU: item.GRUBU || item.grubu,
          DURUMU: item.DURUMU || item.durumu,
          KARTRESIM: item.KARTRESIM,
          UYENO: Number(item.UYENO || item.uyeno),
          borclar: item.borclar || item.borc_detay || [],
          toplam_borc: Number(item.toplam_borc || 0)
        }));
        setKullanici(mapped);
        
        // Durumu session'a kaydet
        const durumu = mapped[0].DURUMU;
        if (durumu) {
          sessionStorage.setItem('durumu', durumu);
          // Sadece durum değiştiyse state'i güncelle
          setSessionInfo(prev => {
            if (prev.durumu !== durumu) {
              return { ...prev, durumu: durumu || '' };
            }
            return prev;
          });
        }
        
        // Eğer kullanıcı UYE grubundaysa ve plaka bilgisi varsa, yakıt ve plaka verilerini çek
        console.log('Kullanıcı grubu:', mapped[0].GRUBU);
        console.log('Plaka bilgisi:', plaka);
        if (mapped[0].GRUBU === 'UYE' && plaka) {
          console.log('UYE grubu ve plaka var, veri çekiliyor...');
          setUserPlaka(plaka);
          await fetchYakitHareketleri(plaka, selectedPeriod);
          await fetchPlakaGecisleri(plaka, selectedPeriod);
        } else if (mapped[0].GRUBU === 'UYE' && !plaka) {
          console.log('UYE grubu ama plaka yok!');
        }
      }
    } catch (err) {
      console.error('Kullanıcı bilgileri getirme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  // Yakıt hareketlerini getir
  const fetchYakitHareketleri = async (plaka: string, period: 'gunluk' | 'haftalik' | 'aylik') => {
    try {
      console.log('Yakıt hareketleri API çağrısı:', { plaka, period });
      const response = await getYakitHareketleri(plaka, period);
      console.log('Yakıt hareketleri API yanıtı:', response);
      if (response.success && response.data) {
        setYakitHareketleri(response.data.hareketler);
        setYakitOzet(response.data.ozet);
      } else {
        console.log('Yakıt hareketleri başarısız:', response.message);
      }
    } catch (err) {
      console.error('Yakıt hareketleri getirme hatası:', err);
    }
  };

  // Plaka geçişlerini getir
  const fetchPlakaGecisleri = async (plaka: string, period: 'gunluk' | 'haftalik' | 'aylik') => {
    try {
      console.log('Plaka geçişleri API çağrısı:', { plaka, period });
      const response = await getPlakaGecisleri(plaka, period);
      console.log('Plaka geçişleri API yanıtı:', response);
      if (response.success && response.data) {
        setPlakaGecisleri(response.data.gecisler);
        setPlakaOzet(response.data.ozet);
      } else {
        console.log('Plaka geçişleri başarısız:', response.message);
      }
    } catch (err) {
      console.error('Plaka geçişleri getirme hatası:', err);
    }
  };

  // Period değiştiğinde verileri yeniden çek
  const handlePeriodChange = async (newPeriod: 'gunluk' | 'haftalik' | 'aylik') => {
    setSelectedPeriod(newPeriod);
    if (userPlaka) {
      setLoading(true);
      try {
        await fetchYakitHareketleri(userPlaka, newPeriod);
        await fetchPlakaGecisleri(userPlaka, newPeriod);
      } finally {
        setLoading(false);
      }
    }
  };

  // Profil menüsünü aç/kapat
  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleLogout = () => {
    clearUserInfo();
    setIsAuthenticated(false);
    setKullanici([]);
    setShowProfileMenu(false);
    navigate('/');
  };

  // Menü öğelerine yönlendirme
  const handleMenuClick = (route: string) => {
    navigate(route);
  };

  // Profil sayfasına yönlendirme
  const handleProfileClick = () => {
    navigate('/profil');
  };

  // Grup adını formatla
  const formatGrupAdi = (grup: string): string => {
    switch(grup) {
      case 'SOFOR': return 'Şoför';
      case 'UYE': return 'Üye';
      case 'YONETICI': return 'Yönetici';
      case 'AKRABA': return 'Akraba';
      case 'DIGERLERI': return 'Diğerleri';
      case 'PASIF': return 'Pasif';
      case 'PERSONEL': return 'Personel';
      default: return grup;
    }
  };

  // Durum adını formatla
  const formatDurumAdi = (durum: string): string => {
    switch(durum) {
      case 'AKTIF': return 'Aktif';
      case 'PASIF': return 'Pasif';
      case 'DEVIR': return 'Devir';
      case 'İHRAÇ': return 'İhraç';
      default: return durum;
    }
  };

  // Durum rengini belirle
  const getDurumClass = (durum: string): string => {
    switch(durum) {
      case 'AKTIF': return 'positive';
      case 'PASIF': return 'negative';
      case 'DEVIR': return 'warning';
      case 'İHRAÇ': return 'danger';
      default: return 'neutral';
    }
  };

  // Para formatı
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Kullanıcı girişi gerektiren menü öğeleri
  const authMenuItems = [
    { id: 'plaka', icon: 'fa-car', title: 'Plaka Listesi', route: '/plaka-listesi' },
    { id: 'borc', icon: 'fa-credit-card', title: 'Borç Ödeme', route: '/odeme' },
    { id: 'tahsilat', icon: 'fa-money-bill-wave', title: 'Tahsilatlar', route: '/tahsilatlar' },
    { id: 'soforler', icon: 'fa-user-tie', title: 'Şoförlerim', route: '/soforlerim' },
  ];

  // UYE grubu için özel menü öğeleri
  const uyeMenuItems = [
    { id: 'plaka-gecislerim', icon: 'fa-exchange-alt', title: 'Plaka Geçişlerim', route: '/plaka-gecislerim' },
    { id: 'yakit-hareketlerim', icon: 'fa-gas-pump', title: 'Yakıt Hareketlerim', route: '/yakit-hareketlerim' },
  ];

  // Tüm kullanıcılar için menü öğeleri
  const publicMenuItems = [
    { id: 'ucuslar', icon: 'fa-plane', title: 'Uçuş Bilgileri', route: '/ucus-bilgileri' },
    { id: 'hava', icon: 'fa-cloud-sun', title: 'Hava Durumu', route: '/hava-durumu' },
    { id: 'trafik', icon: 'fa-map-marked-alt', title: 'Trafik Haritası', route: '/trafik-haritasi' },
    { id: 'taksimetre', icon: 'fa-calculator', title: 'Taksimetre Hesaplama', route: '/taksimetre' },
    { id: 'istek', icon: 'fa-clipboard-list', title: 'İstek/Şikayet', route: '/istek-sikayet' },
    { id: 'duyuru', icon: 'fa-bullhorn', title: 'Duyurular', route: '/duyurular' },
    { id: 'iletisim', icon: 'fa-phone-alt', title: 'İletişim', route: '/iletisim' },
    { id: 'baskan', icon: 'fa-user-shield', title: 'Başkan', route: '/baskan' }
  ];

  // Otomatik carousel geçişi
  useEffect(() => {
    if (activeTab === 'borclar' && kullanici.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % kullanici.length);
      }, 5000); // 5 saniyede bir geçiş
      return () => clearInterval(interval);
    }
  }, [activeTab, kullanici.length]);

  // Window resize listener ve kart boyutu hesaplama
  useEffect(() => {
    const updateCardSize = () => {
      if (window.innerWidth <= 480) {
        setCardWidth(300); // Mobilde 300px
        setCardGap(18);
      } else if (window.innerWidth <= 768) {
        setCardWidth(300); // Tablet'te 300px
        setCardGap(20);
      } else {
        setCardWidth(320); // Desktop'ta 320px
        setCardGap(24);
      }
    };

    updateCardSize();
    window.addEventListener('resize', updateCardSize);
    return () => window.removeEventListener('resize', updateCardSize);
  }, []);

  // Carousel kaydırma fonksiyonları
  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % kullanici.length);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + kullanici.length) % kullanici.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="home-container">
      {/* Header */}
      <header className="home-header">
        <img 
          src="/images/logo2024.png" 
          alt="Logo" 
          className="header-logo" 
          onClick={() => navigate('/')}
        />
        <div className="header-right">
          {isAuthenticated ? (
            <>
              <button className="notification-btn" onClick={() => handleMenuClick('/bildirimler')}>
                <i className="fas fa-bell"></i> {bildirimSayisi > 0 && <span className="notification-badge">{bildirimSayisi}</span>}
              </button>
              <div className="profile-menu-container">
                <button className="profile-btn" onClick={toggleProfileMenu}>
                  <img 
                    src={kullanici.length > 0 && kullanici[0].KARTRESIM ? `https://crm.havalimanitaksi.com.tr/media/natural/${kullanici[0].KARTRESIM}` : '/images/default-profile.png'} 
                    alt="Profil" 
                    className="profile-image"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/images/default-user.png';
                    }}
                  />
                </button>
                {showProfileMenu && (
                  <div className="profile-menu">
                    <button onClick={() => handleMenuClick('/profil')}>Profil</button>
                    <button onClick={() => handleMenuClick('/ayarlar')}>Ayarlar</button>
                    <button onClick={handleLogout}>Çıkış</button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button 
              className="login-btn" 
              onClick={() => navigate('/login')}
            >
              <i className="fas fa-sign-in-alt"></i> Üye Girişi
            </button>
          )}
        </div>
      </header>

      {loading ? (
        <TaxiLoader message="Bilgiler yükleniyor..." />
      ) : (
        <Container className="main-content">
          {/* Kullanıcı Bilgi Kartı - Sadece giriş yapmış kullanıcılara göster */}
          {isAuthenticated && kullanici.length > 0 ? (
            <section className="user-info-cards">
              {/* Tab Menüsü */}
              <div className="tab-menu-wrapper">
                <div className="tab-menu">
                  <button 
                    className={`tab-btn ${activeTab === 'borclar' ? 'active' : ''}`}
                    onClick={() => setActiveTab('borclar')}
                    data-tab="borclar"
                  >
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                    <span className="tab-text">BORÇLARIM</span>
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'plaka' ? 'active' : ''}`}
                    onClick={() => setActiveTab('plaka')}
                    data-tab="plaka"
                  >
                    <FontAwesomeIcon icon={faExchangeAlt} />
                    <span className="tab-text">PLAKA GEÇİŞLERİM</span>
                  </button>
                  <button 
                    className={`tab-btn ${activeTab === 'yakit' ? 'active' : ''}`}
                    onClick={() => setActiveTab('yakit')}
                    data-tab="yakit"
                  >
                    <FontAwesomeIcon icon={faGasPump} />
                    <span className="tab-text">YAKIT HAREKETLERİM</span>
                  </button>
                </div>
              </div>

              {/* Tab İçerikleri */}
              <div className="tab-content">
                {activeTab === 'borclar' && (
                  <div className="modern-carousel-container">
                    {kullanici.length > 1 && (
                      <button className="carousel-btn carousel-btn-prev" onClick={prevSlide}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                    )}
                    
                    <div 
                      className="modern-carousel"
                      style={{
                        transform: `translateX(-${currentSlide * (cardWidth + cardGap)}px)`,
                        transition: 'transform 0.5s ease-in-out'
                      }}
                    >
                      {kullanici.map((item, idx) => (
                        <div className="modern-card" key={idx}>
                          <div className="card-gradient-bg"></div>
                          <div className="card-content">
                            <div className="profile-section">
                              <div className="profile-frame">
                                <img 
                                  src={item.KARTRESIM ? `https://crm.havalimanitaksi.com.tr/media/natural/${item.KARTRESIM}` : '/images/default-profile.png'} 
                                  alt="Profil" 
                                  className="profile-image-modern"
                                  onError={e => { (e.target as HTMLImageElement).src = '/images/default-profile.png'; }}
                                />
                                <div className="profile-status-indicator"></div>
                              </div>
                              <div className="profile-info">
                                <h3 className="profile-name">{item.ADI} {item.SOYADI}</h3>
                                <div className="profile-badges">
                                  <span className="badge badge-group">{formatGrupAdi(item.GRUBU)}</span>
                                  <span className={`badge badge-status ${getDurumClass(item.DURUMU)}`}>
                                    {formatDurumAdi(item.DURUMU)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="debt-section">
                              <div className="debt-amount-container">
                                <FontAwesomeIcon icon={faMoneyBillWave} className="debt-icon" />
                                <div className="debt-details">
                                  <span className="debt-label">Toplam Borç</span>
                                  <span className="debt-amount">{formatCurrency(Number(item.toplam_borc || 0))}</span>
                                </div>
                              </div>
                              
                              {Number(item.toplam_borc) > 0 ? (
                                <button className="pay-button-modern" onClick={() => navigate('/odeme')}>
                                  <FontAwesomeIcon icon={faCreditCard} />
                                  <span>Borç Öde</span>
                                  <FontAwesomeIcon icon={faChevronRight} className="arrow-icon" />
                                </button>
                              ) : (
                                <div className="no-debt-indicator">
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                  <span>Borç Yok</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {kullanici.length > 1 && (
                      <button className="carousel-btn carousel-btn-next" onClick={nextSlide}>
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    )}
                    
                    {kullanici.length > 1 && (
                      <div className="carousel-indicators">
                        {kullanici.map((_, idx) => (
                          <div 
                            key={idx} 
                            className={`indicator ${idx === currentSlide ? 'active' : ''}`}
                            onClick={() => goToSlide(idx)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'plaka' && (
                  <div className="plaka-istatistik-content">
                    {userPlaka ? (
                      <>
                        {/* Period Seçim Butonları */}
                        <div className="period-selector">
                          <button 
                            className={`period-btn ${selectedPeriod === 'gunluk' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('gunluk')}
                          >
                            Günlük
                          </button>
                          <button 
                            className={`period-btn ${selectedPeriod === 'haftalik' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('haftalik')}
                          >
                            Haftalık
                          </button>
                          <button 
                            className={`period-btn ${selectedPeriod === 'aylik' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('aylik')}
                          >
                            Aylık
                          </button>
                        </div>

                        {/* Özet İstatistik Kartları */}
                        <div className="stats-cards">
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faCar} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{plakaOzet.giris_sayisi}</div>
                              <div className="stat-label">Giriş</div>
                            </div>
                          </div>
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faExchangeAlt} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{plakaOzet.cikis_sayisi}</div>
                              <div className="stat-label">Çıkış</div>
                            </div>
                          </div>
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faCheckCircle} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{plakaOzet.toplam_gecis}</div>
                              <div className="stat-label">Toplam Geçiş</div>
                            </div>
                          </div>
                        </div>

                        {/* Detay Görüntüleme Butonu */}
                        <div className="detail-button-container">
                          <button 
                            className="detail-view-btn"
                            onClick={() => navigate('/plaka-gecislerim')}
                          >
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>Plaka Geçişlerimi Görüntüle</span>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="no-plaka">
                        <FontAwesomeIcon icon={faCar} style={{fontSize:'3rem',color:'#666',marginBottom:16}}/>
                        <h3>Plaka Bilgisi Bulunamadı</h3>
                        <p>Plaka geçişlerini görüntülemek için plaka bilginiz sistemde kayıtlı olmalıdır.</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'yakit' && (
                  <div className="yakit-hareketleri-content">
                    {userPlaka ? (
                      <>
                        {/* Period Seçim Butonları */}
                        <div className="period-selector">
                          <button 
                            className={`period-btn ${selectedPeriod === 'gunluk' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('gunluk')}
                          >
                            Günlük
                          </button>
                          <button 
                            className={`period-btn ${selectedPeriod === 'haftalik' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('haftalik')}
                          >
                            Haftalık
                          </button>
                          <button 
                            className={`period-btn ${selectedPeriod === 'aylik' ? 'active' : ''}`}
                            onClick={() => handlePeriodChange('aylik')}
                          >
                            Aylık
                          </button>
                        </div>

                        {/* Özet Kartları */}
                        <div className="stats-cards">
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faGasPump} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{yakitOzet.toplam_litre.toFixed(2)}</div>
                              <div className="stat-label">Litre</div>
                            </div>
                          </div>
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{formatCurrency(yakitOzet.toplam_tutar)}</div>
                              <div className="stat-label">Toplam Tutar</div>
                            </div>
                          </div>
                          <div className="stat-card">
                            <FontAwesomeIcon icon={faCheckCircle} className="stat-icon" />
                            <div className="stat-content">
                              <div className="stat-number">{yakitOzet.hareket_sayisi}</div>
                              <div className="stat-label">İşlem Sayısı</div>
                            </div>
                          </div>
                        </div>

                        {/* Detay Görüntüleme Butonu */}
                        <div className="detail-button-container">
                          <button 
                            className="detail-view-btn"
                            onClick={() => navigate('/yakit-hareketlerim')}
                          >
                            <FontAwesomeIcon icon={faGasPump} />
                            <span>Yakıt Hareketlerimi Görüntüle</span>
                            <FontAwesomeIcon icon={faChevronRight} />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="no-plaka">
                        <FontAwesomeIcon icon={faGasPump} style={{fontSize:'3rem',color:'#666',marginBottom:16}}/>
                        <h3>Plaka Bilgisi Bulunamadı</h3>
                        <p>Yakıt hareketlerini görüntülemek için plaka bilginiz sistemde kayıtlı olmalıdır.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          ) : !isAuthenticated ? (
            <section className="welcome-banner">
              <div className="welcome-content">
                <h1>İstanbul Havalimanı Taksi Kooperatifi'ne Hoş Geldiniz</h1>
                <p>Üye girişi yaparak borç sorgulama, ödeme ve diğer üye özelliklerine erişebilirsiniz.</p>
                <button 
                  className="login-button-large"
                  onClick={() => navigate('/login')}
                >
                  <i className="fas fa-sign-in-alt"></i> Üye Girişi Yap
                </button>
              </div>
              <div className="welcome-image">
                <img src="/images/taxi-illustration.png" alt="Taksi İllüstrasyonu" />
              </div>
            </section>
          ) : null}

          {/* İstanbul Hava Durumu ve Trafik Haritası */}
          <section className="info-widgets">
            <div className="widget-row">
              <div className="widget weather-widget">
                <div className="widget-header">
                  <h3><i className="fas fa-cloud-sun"></i> İstanbul Hava Durumu</h3>
                </div>
                <div className="widget-content">
                  <div className="weather-preview">
                    <div className="weather-icon"><i className="fas fa-cloud-sun"></i></div>
                    <div className="weather-info">
                      <div className="weather-temp">18°C</div>
                      <div className="weather-desc">Parçalı Bulutlu</div>
                    </div>
                  </div>
                  <button 
                    className="widget-button"
                    onClick={() => handleMenuClick('/hava-durumu')}
                  >
                    Detaylı Hava Durumu
                  </button>
                </div>
              </div>

              <div className="widget traffic-widget">
                <div className="widget-header">
                  <h3><i className="fas fa-map-marked-alt"></i> İstanbul Trafik Durumu</h3>
                </div>
                <div className="widget-content">
                  <div className="traffic-preview">
                    <div className="traffic-icon"><i className="fas fa-car"></i></div>
                    <div className="traffic-text">İstanbul trafik durumunu görüntüleyin</div>
                  </div>
                  <button 
                    className="widget-button"
                    onClick={() => handleMenuClick('/trafik-haritasi')}
                  >
                    Trafik Haritasını Görüntüle
                  </button>
                </div>
              </div>
            </div>

            <div className="widget taksimetre-widget">
              <div className="widget-header">
                <h3><i className="fas fa-calculator"></i> Taksimetre Hesaplama</h3>
              </div>
              <div className="widget-content">
                <div className="taksimetre-preview">
                  <p>Gideceğiniz mesafeye göre yaklaşık taksi ücretini hesaplayın.</p>
                </div>
                <button 
                  className="widget-button"
                  onClick={() => handleMenuClick('/taksimetre')}
                >
                  Taksimetre Hesapla
                </button>
              </div>
            </div>
          </section>

         

          {/* Menü alanı - Genel menüleri herkese göster */}
          <section className="menu-section">
            <div className="menu-grid">
              {/* Üye İşlemleri - Sadece giriş yapmış kullanıcılara */}
            {isAuthenticated && (
                <div className="auth-menu-section">
                  <h3 className="menu-section-title">
                    <FontAwesomeIcon icon={faUserTie} />
                    Üye İşlemleri
                  </h3>
                  <div className="menu-items">
                    {authMenuItems.map((item) => (
                      <button
                        key={item.id}
                        className="menu-item"
                        data-category="financial"
                        onClick={() => navigate(item.route)}
                      >
                        <div className="menu-icon">
                          <FontAwesomeIcon icon={
                            item.id === 'plaka' ? faCar :
                            item.id === 'borc' ? faCreditCard :
                            item.id === 'tahsilat' ? faMoneyBillWave :
                            faClipboardList
                          } />
                        </div>
                        <span className="menu-title">{item.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* UYE Grubu Özel Menüleri - Sadece UYE grubundaki kullanıcılara */}
              {isAuthenticated && kullanici.length > 0 && kullanici[0].GRUBU === 'UYE' && (
                <div className="uyespecial-menu-section">
                  <h3 className="menu-section-title">
                    <FontAwesomeIcon icon={faUserTie} />
                    Üye Özel İşlemler
                  </h3>
                  <div className="menu-items">
                    {uyeMenuItems.map((item) => (
                      <button
                        key={item.id}
                        className="menu-item"
                        data-category="special"
                        onClick={() => navigate(item.route)}
                      >
                        <div className="menu-icon">
                          <FontAwesomeIcon icon={
                            item.id === 'plaka-gecislerim' ? faExchangeAlt :
                            item.id === 'yakit-hareketlerim' ? faGasPump :
                            faClipboardList
                          } />
                        </div>
                        <span className="menu-title">{item.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Genel Hizmetler - Herkese açık */}
                <div className="public-menu-section">
                  <h3 className="menu-section-title">
                    <FontAwesomeIcon icon={faMapMarkedAlt} />
                    Genel Hizmetler
                  </h3>
                  <div className="menu-items">
                    {publicMenuItems.map((item) => (
                      <button
                        key={item.id}
                        className="menu-item"
                        data-category={
                        ['ucuslar', 'hava'].includes(item.id) ? 'info' :
                        ['trafik', 'taksimetre'].includes(item.id) ? 'service' :
                        ['istek', 'duyuru', 'iletisim', 'baskan'].includes(item.id) ? 'admin' :
                          'service'
                        }
                        onClick={() => navigate(item.route)}
                      >
                        <div className="menu-icon">
                          <FontAwesomeIcon icon={
                          item.id === 'ucuslar' ? faPlane :
                          item.id === 'hava' ? faCloudSun :
                          item.id === 'trafik' ? faMapMarkedAlt :
                            item.id === 'taksimetre' ? faCalculator :
                          item.id === 'istek' ? faClipboardList :
                          item.id === 'duyuru' ? faBullhorn :
                            item.id === 'iletisim' ? faPhoneAlt :
                            item.id === 'baskan' ? faUserShield :
                            faMapMarkedAlt
                          } />
                        </div>
                        <span className="menu-title">{item.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
          </section>
        </Container>
      )}

      {/* Tab Bar Bileşeni */}
      <TabBar />
    </div>
  );
};

export default Home; 